import { CommonModule } from '@angular/common';
import {
  Component, OnDestroy, OnInit
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterModule
} from '@angular/router';

import { Subject, takeUntil } from 'rxjs';

import { USStateAbbrevMap } from '@sondermind/constants';
import { PublicFooterModule } from '@sondermind/features/app-footer';
import { PublicHeaderModule } from '@sondermind/features/app-header';
import { ENVIRONMENT } from '@sondermind/utilities/models-environment';
import {
  EmptyURIValue,
  fixedEncodeURIComponent,
  URIPrefix
} from '@sondermind/features/filter-form';

import { IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';
import {
  BrowseLinkKeys,
  BrowseLinkKeyType,
  IBrowseLinkApiResponse,
  IBrowseLinkData
} from '@sondermind/features/provider-listing-card';
import { environment } from '../../../environments/environment';
import { BrowseStateHttpService } from '../../services/browse-state.httpservice';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    PublicFooterModule,
    PublicHeaderModule,
    RouterModule
  ],
  selector: 'browse-state-page',
  providers: [
    { provide: ENVIRONMENT, useValue: environment }
  ],
  templateUrl: './browse-state-page.component.html',
  styleUrls: ['./browse-state-page.component.scss'],
})

export class BrowseStatePage implements OnDestroy, OnInit {
  selectedState: string = '';
  selectedStateAbbrev: string = '';
  uriPrefix = URIPrefix;
  emptyURIValue = EmptyURIValue;

  destroyed$ = new Subject<void>();
  specialities: IBrowseLinkData[];
  insurances: IBrowseLinkData[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private browseStateHttpService: BrowseStateHttpService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((paramMap) => {
      const stateParam: string | null = paramMap.get('state')?.split('-')[1] ?? null;

      if (stateParam) {
        this.navigateToLowerCaseState(stateParam);
        this.handleStateName(stateParam.toUpperCase());
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getBrowseLinks(): void {
    const options: IAPIListRequestOptions = {
      page: 'disabled',
      filter: { key: 'state', value: this.selectedStateAbbrev },
    };

    this.browseStateHttpService.getBrowseLinks(options)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ data }: IAPIListResponse<IBrowseLinkApiResponse>) => {
        this.specialities = this.getTopItems(data, BrowseLinkKeys.SPECIALTY, 5);
        this.insurances = this.getTopItems(data, BrowseLinkKeys.INSURANCE, 5);
      });
  }

  private navigateToLowerCaseState(stateParam: string): void {
    const lowerCaseState = stateParam.toLowerCase();

    // Redirect to lowercase state if the URL contains uppercase state abbreviation
    if (stateParam !== lowerCaseState) {
      this.router.navigate(['/browse', `${URIPrefix.STATE}-${lowerCaseState}`], { replaceUrl: true });
    }
  }

  private handleStateName(state: string): void {
    this.selectedState = USStateAbbrevMap[state] ?? state;
    this.selectedStateAbbrev = state;
    this.getBrowseLinks();
  }

  // Filter then sort by provider_count in descending order
  // And slice given count
  private getTopItems(items: IBrowseLinkApiResponse[], key: BrowseLinkKeyType, count: number): IBrowseLinkData[] {
    return items
      .filter((item) => item[key] !== null)
      .sort((a, b) => b.provider_count - a.provider_count)
      .slice(0, count)
      .map((item) => {
        const value = item[key];
        return {
          value,
          uriSafeValue: fixedEncodeURIComponent(value.toLocaleLowerCase())
        };
      });
  }
}
